import autoTable from 'jspdf-autotable'

export const PAGE = { width: 612, height: 792, margin: 40 }
export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2
export const BOTTOM = PAGE.height - 52

export const GRAY = [108, 117, 125]
export const LIGHT = [233, 236, 239]
export const DARK = [33, 37, 41]
export const WHITE = [255, 255, 255]

export const TONES = {
  success: [46, 125, 50],
  info: [2, 119, 189],
  warning: [230, 120, 0],
  error: [211, 47, 47],
  neutral: GRAY,
}

export const DENSITIES = [
  { font: 9, small: 8, pad: 6, headPad: 7, gap: 22, pill: 16, teacherLine: true, comments: true, commentFont: 8.5 },
  { font: 8.5, small: 7.5, pad: 4.5, headPad: 5.5, gap: 18, pill: 14, teacherLine: true, comments: true, commentFont: 8 },
  { font: 8, small: 7, pad: 3, headPad: 4, gap: 14, pill: 12, teacherLine: true, comments: true, commentFont: 7.5 },
  { font: 7.5, small: 6.5, pad: 2, headPad: 3, gap: 11, pill: 11, teacherLine: false, comments: true, commentFont: 7 },
  { font: 7, small: 6, pad: 1.5, headPad: 2.5, gap: 9, pill: 10, teacherLine: false, comments: false, commentFont: 6.5 },
]

const DEFAULT_PRIMARY = [25, 118, 210]

// Space before a section heading
export const sectionGap = d => Math.max(d.gap, 18)

export const hexToRgb = color => {
  const hex = String(color ?? '').replace('#', '')
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
  if (!/^[0-9a-f]{6}$/i.test(full)) return DEFAULT_PRIMARY
  return [0, 2, 4].map(index => parseInt(full.slice(index, index + 2), 16))
}

export const tint = (rgb, amount) => rgb.map(value => Math.round(value + (255 - value) * amount))

export const fullName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ')

export const teacherNames = course => (course.teachers ?? []).map(fullName).join(', ')

export const courseName = course => course.course?.name ?? course.courseSession?.name ?? ''

const PERIOD_WORDS = [
  [/\bmarking period\b/i, 'MP'],
  [/\bquarter\b/i, 'Q'],
  [/\bsemester\b/i, 'Sem'],
  [/\btrimester\b/i, 'Tri'],
  [/\bterm\b/i, 'T'],
]

// "1st Quarter" → "1st Q", for narrow grade columns and tiles
export const shortPeriodName = name =>
  PERIOD_WORDS.reduce((text, [pattern, short]) => text.replace(pattern, short), String(name ?? '')).trim()

// Header cell of a narrow per-period column: one line, cut with an ellipsis if still too long
export const periodHead = name => ({
  content: shortPeriodName(name),
  styles: { halign: 'center', overflow: 'ellipsize', cellPadding: { top: 4, bottom: 4, left: 3, right: 3 } },
})

export const gradeText = grade => grade?.letterGrade ?? (grade?.grade != null ? String(grade.grade) : null)

export const periodGrade = (course, period) =>
  gradeText(course.grades?.find(grade => grade.markingPeriodId === period.id)) ?? '—'

export const periodComment = (course, period) =>
  period ? course.grades?.find(grade => grade.markingPeriodId === period.id)?.comment : null

export const gradeTone = text => {
  const letter = String(text ?? '').trim().toUpperCase()[0]
  if (letter === 'A') return TONES.success
  if (letter === 'B') return TONES.info
  if (letter === 'C') return TONES.warning
  if (letter === 'D' || letter === 'F') return TONES.error
  return TONES.neutral
}

export const number = (value, digits = 2) => (value == null ? '—' : Number(value).toFixed(digits))

export const attendanceTone = rate =>
  rate == null || rate >= 95 ? TONES.success : rate >= 90 ? TONES.warning : TONES.error

export const attendanceKinds = t => [
  { key: 'present', label: t('Present'), tone: TONES.success },
  { key: 'absent', label: t('Absent'), tone: TONES.error },
  { key: 'tardy', label: t('Tardy'), tone: TONES.warning },
  { key: 'excused', label: t('Excused'), tone: TONES.info },
]

export const periodAttendance = (attendance, period) =>
  attendance?.markingPeriods?.find(item => item.markingPeriodId === period.id) ?? {}

export const periodGpa = (gpa, period) => gpa?.markingPeriods?.find(item => item.markingPeriodId === period.id)?.gpa

// Same alias on every page so the file holds the image once; 'FAST' compresses its pixels
export const drawLogo = (doc, logo, x, y, width, height) =>
  doc.addImage(logo.dataUrl, 'PNG', x, y, width, height, 'district-logo', 'FAST')

export const kpi = (doc, { x, y, width, height = 28, label, value, tone, filled }) => {
  doc.setFillColor(...(filled ? tone : tint(tone, 0.9)))
  doc.roundedRect(x, y, width, height, 4, 4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(...(filled ? WHITE : tone))
  doc.text(String(value), x + width / 2, y + height * 0.45, { align: 'center' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(5.5)
  doc.setTextColor(...(filled ? WHITE : GRAY))
  doc.text(label.toUpperCase(), x + width / 2, y + height * 0.79, { align: 'center' })
}

export const pill = (doc, text, x, y, tone, height = 16) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(Math.min(9, height * 0.58))
  const width = Math.max(doc.getTextWidth(text) + height * 0.9, height * 1.9)
  doc.setFillColor(...tint(tone, 0.86))
  doc.roundedRect(x - width / 2, y - height / 2, width, height, height / 2, height / 2, 'F')
  doc.setTextColor(...tone)
  doc.text(text, x, y + height * 0.2, { align: 'center' })
}

// Draws "course name" bold with the teachers under it (or after it when the density has no teacher line)
export const drawCourseCell = (doc, cell, d, family = 'helvetica') => {
  const { name, teachers } = cell.raw
  const x = cell.x + cell.padding('left')
  const width = cell.width - cell.padding('left') - cell.padding('right')
  let lineY = cell.y + cell.padding('top') + d.font * 0.95

  doc.setFont(family, 'bold')
  doc.setFontSize(d.font + 0.5)
  doc.setTextColor(...DARK)
  const lines = doc.splitTextToSize(name, width)
  lines.forEach((line, index) => {
    doc.text(line, x, lineY)
    if (index < lines.length - 1) lineY += d.font * 1.25
  })

  if (!teachers) return
  doc.setFont(family, 'normal')
  doc.setFontSize(d.small)
  doc.setTextColor(...GRAY)
  if (d.teacherLine) {
    doc.text(teachers, x, lineY + d.font * 1.25, { maxWidth: width })
  } else {
    doc.setFont(family, 'bold')
    doc.setFontSize(d.font + 0.5)
    const nameWidth = doc.getTextWidth(lines[lines.length - 1])
    doc.setFont(family, 'normal')
    doc.setFontSize(d.small)
    const room = width - nameWidth - 8
    if (room > 30) doc.text(doc.splitTextToSize(teachers, room)[0], x + nameWidth + 8, lineY)
  }
}

export const courseCell = (course, d) => {
  const name = courseName(course)
  const teachers = teacherNames(course)
  return {
    content: d.teacherLine && teachers ? `${name}\n${teachers}` : name,
    kind: 'course',
    name,
    teachers,
    // measured in the same font drawCourseCell uses, so a wrapped name gets the rows it needs
    styles: { fontStyle: 'bold', fontSize: d.font + 0.5 },
  }
}

export const RADIUS = 3

export const condensed = d => ({
  ...d,
  pad: Math.max(Math.min(d.pad * 0.55, 3.5), 1.5),
  headPad: Math.max(Math.min(d.headPad * 0.65, 4.5), 2.5),
  pill: Math.min(d.pill, 13),
})

const upperHead = head =>
  head.map(row =>
    row.map(cell => (typeof cell === 'string' ? cell.toUpperCase() : { ...cell, content: String(cell.content).toUpperCase() })),
  )

export const cardTable = (
  doc,
  d,
  look,
  { head, body, x = PAGE.margin, width = CONTENT_WIDTH, top, skipDivider = () => false, willDrawCell, didDrawCell, ...options },
) => {
  const { startY } = options
  const lastRow = body.length - 1
  const inset = look.inset ?? 8
  const padding = { left: 10, right: 10 }

  autoTable(doc, {
    theme: 'plain',
    tableWidth: width,
    margin: { left: x, right: PAGE.width - x - width, top: top ?? startY, bottom: PAGE.height - BOTTOM },
    styles: { font: look.font ?? 'helvetica', fontSize: d.font, cellPadding: { top: d.pad, bottom: d.pad, ...padding }, textColor: DARK },
    headStyles: {
      fillColor: false,
      textColor: look.headText,
      fontStyle: 'bold',
      fontSize: look.headFontSize ?? 7,
      cellPadding: { top: d.headPad + 1, bottom: d.headPad + 1, ...padding },
      valign: 'middle',
    },
    head: upperHead(head),
    body,
    ...options,
    willDrawCell: data => {
      if (data.section === 'head' && data.column.index === 0) {
        const height = data.row.height
        doc.setFillColor(...look.headFill)
        doc.roundedRect(x, data.cell.y, width, height, RADIUS, RADIUS, 'F')
        doc.rect(x, data.cell.y + height / 2, width, height / 2, 'F')
      }
      willDrawCell?.(data)
    },
    didDrawCell: data => {
      didDrawCell?.(data)
      const { cell, column, row, section, table } = data
      doc.setDrawColor(...look.divider)
      doc.setLineWidth(0.5)
      if (look.columnLines && column.index < table.columns.length - 1 && (cell.colSpan ?? 1) === 1) {
        doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height)
      }
      if (section === 'body' && column.index === 0 && row.index < lastRow && !skipDivider(data)) {
        doc.line(x + inset, cell.y + cell.height, x + width - inset, cell.y + cell.height)
      }
    },
  })

  const { finalY } = doc.lastAutoTable
  doc.setDrawColor(...look.outline)
  doc.setLineWidth(0.7)
  doc.roundedRect(x, startY, width, finalY - startY, RADIUS, RADIUS, 'S')
  return finalY
}
